// ─────────────────────────────────────────────────────────────────────────────
// The library: everything this tenant has stored, to pick from or manage.
//
// Uploading the same logo for the fourth time is the failure this prevents.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import { media } from '@repletoryreach/sdk-api';
import { Button, Stack, Group, Text, Loader, Center } from '@repletoryreach/sdk-ui';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { openFileViewer } from '@repletoryreach/sdk-ui/file-viewer';
import { mediaViewerSource } from './viewer.js';
import { MediaThumb } from './MediaPreview.js';
import { describe } from './useMediaUpload.js';
import {
  DEFAULT_LABELS, formatBytes, toMediaValue,
  type FileUploadLabels, type MediaValue
} from './types.js';

export interface MediaLibraryProps {
  /** Called with the chosen file. */
  onSelect: (value: MediaValue) => void;
  /** MIME prefix filter, e.g. `image/`. Blank shows everything. */
  mime?: string;
  /** Show the filter row. Default true when `mime` is not fixed by the caller. */
  withFilters?: boolean;
  /** Permanent delete, from inside the picker. Default true. */
  allowDelete?: boolean;
  labels?: Partial<FileUploadLabels>;
  /** Rows per page. Default 24. */
  pageSize?: number;
}

const PAGE = 24;

export function MediaLibrary({
  onSelect, mime, withFilters, allowDelete = true, labels, pageSize = PAGE
}: MediaLibraryProps) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const showFilters = withFilters ?? !mime;

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(mime ?? '');
  const [rows, setRows] = useState<MediaValue[]>([]);
  const [offset, setOffset] = useState(0);
  const [more, setMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (from: number, term: string, kind: string, signal: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const res = await media.list({
        search: term || undefined,
        mime: kind || undefined,
        limit: pageSize,
        offset: from,
        signal
      });
      if (signal.aborted) return;
      const page = (res.data ?? []).map(toMediaValue);
      setRows((prev) => (from === 0 ? page : [...prev, ...page]));
      setMore(page.length === pageSize);
      setOffset(from + page.length);
    } catch (e) {
      if (signal.aborted) return;
      setError(describe(e, labels));
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [pageSize, labels]);

  // Typing re-runs the query, debounced: a search box that fires per keystroke
  // makes the service do six lookups for a five-letter word.
  useEffect(() => {
    const ac = new AbortController();
    const timer = setTimeout(() => { void load(0, search, filter, ac.signal); }, search ? 250 : 0);
    return () => { clearTimeout(timer); ac.abort(); };
  }, [search, filter, load]);

  const loadMore = () => {
    const ac = new AbortController();
    void load(offset, search, filter, ac.signal);
  };

  const destroy = (row: MediaValue) => {
    openConfirmModal({
      title: L.deleteFile,
      children: L.deleteConfirm,
      labels: { confirm: L.deleteFile, cancel: L.cancel },
      confirmProps: { color: 'error' },
      onConfirm: () => {
        media.remove(row.id)
          .then(() => setRows((prev) => prev.filter((r) => r.id !== row.id)))
          .catch((e: unknown) => setError(describe(e, labels)));
      }
    });
  };

  return (
    <Stack gap="sm">
      <Group gap="sm" wrap="wrap">
        <TextInput
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder={L.librarySearch}
          aria-label={L.librarySearch}
          className="rr-media-lib__search"
        />
        {showFilters && (
          <Group gap={6}>
            {([['', L.filterAll], ['image/', L.filterImage], ['video/', L.filterVideo], ['application/', L.filterDocument]] as const).map(
              ([value, text]) => (
                <Button
                  key={value || 'all'}
                  size="xs"
                  variant={filter === value ? 'filled' : 'subtle'}
                  onClick={() => setFilter(value)}
                >
                  {text}
                </Button>
              )
            )}
          </Group>
        )}
      </Group>

      {error && <Alert color="error" role="alert">{error}</Alert>}

      {rows.length === 0 && !loading && !error && (
        <Center style={{ padding: '32px 0' }}>
          <Text role="bodySmall" c="dimmed">{L.libraryEmpty}</Text>
        </Center>
      )}

      <div className="rr-media-lib__grid">
        {rows.map((row) => (
          <div key={row.id} className="rr-media-lib__cell">
            <button
              type="button"
              className="rr-media-lib__pick"
              onClick={() => onSelect(row)}
              title={row.original_name ?? ''}
              aria-label={`${L.select}: ${row.original_name ?? row.id}`}
            >
              <MediaThumb value={row} size={92} />
              <span className="rr-media-lib__name">{row.original_name ?? row.id}</span>
              <span className="rr-media-lib__meta">{formatBytes(row.size_bytes)}</span>
            </button>
            <button
              type="button"
              className="rr-media-lib__view"
              onClick={() => openFileViewer(mediaViewerSource(row))}
              aria-label={`${L.view}: ${row.original_name ?? row.id}`}
              title={L.view}
            >
              <EyeIcon />
            </button>
            {allowDelete && (
              <button
                type="button"
                className="rr-media-lib__del"
                onClick={() => destroy(row)}
                aria-label={`${L.deleteFile}: ${row.original_name ?? row.id}`}
                title={L.deleteFile}
              >
                <TrashIcon />
              </button>
            )}
          </div>
        ))}
      </div>

      {loading && <Center style={{ padding: 12 }}><Loader size="sm" /></Center>}

      {more && !loading && (
        <Center>
          <Button variant="subtle" size="sm" onClick={loadMore}>{L.libraryLoadMore}</Button>
        </Center>
      )}
    </Stack>
  );
}

function EyeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1.5 8s2.4-4.5 6.5-4.5S14.5 8 14.5 8s-2.4 4.5-6.5 4.5S1.5 8 1.5 8z" /><circle cx="8" cy="8" r="2" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2.8 4.4h10.4M6.4 4.4V3.1h3.2v1.3M4.2 4.4l.6 8.5h6.4l.6-8.5" />
    </svg>
  );
}
